import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import {
  PageHeader, Table, Th, Td, TrEmpty, Badge, LoadingState, ErrorState,
} from '../components/ui';

interface Subscription {
  id: string;
  plan: string;
  seatLimit: number | null;
  suspendedAt: string | null;
}

const PLAN_VARIANT: Record<string, 'default' | 'info' | 'success'> = {
  free:       'default',
  starter:    'info',
  pro:        'success',
  enterprise: 'success',
};

export function Subscriptions() {
  const { data, isLoading, error } = useQuery<{ subscriptions: Subscription[] }>({
    queryKey: ['admin', 'subscriptions'],
    queryFn: () => api.get('/subscriptions'),
  });

  const subs = data?.subscriptions ?? [];

  return (
    <div>
      <PageHeader
        title="Subscriptions"
        subtitle={data ? `${subs.length} total` : undefined}
      />

      {isLoading && <LoadingState />}
      {error && <ErrorState message="Error loading subscriptions" />}

      {!isLoading && !error && (
        <Table>
          <thead>
            <tr>
              <Th>Entity ID</Th>
              <Th>Plan</Th>
              <Th>Seats</Th>
              <Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {subs.length === 0 ? (
              <TrEmpty cols={4} message="No subscriptions found" />
            ) : (
              subs.map(s => (
                <tr key={s.id} className="hover:bg-white/[0.02] transition-colors">
                  <Td className="font-mono text-xs text-gray-400">{s.id}</Td>
                  <Td>
                    <Badge variant={PLAN_VARIANT[s.plan] ?? 'default'}>{s.plan}</Badge>
                  </Td>
                  <Td className="text-gray-300">
                    {s.seatLimit !== null ? s.seatLimit : <span className="text-gray-500">Unlimited</span>}
                  </Td>
                  <Td>
                    {s.suspendedAt ? (
                      <Badge variant="danger">Suspended</Badge>
                    ) : (
                      <Badge variant="success">Active</Badge>
                    )}
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}
    </div>
  );
}
